import type { AxiosError, AxiosResponse } from 'axios'
import { isAxiosError } from 'axios'
import qs from 'qs'

import request from '../requesters/accounting'
import requestV2 from '../requesters/accountingV2'
import { GraphqlException } from '../resolvers/util'

export const getLedgersByCaseId = async (input: QueryGetLedgersByCaseIdArgs) => {
  const response = await request.get<CaseLedgers>(`/accounting/ledger/case/${input.caseId}`)
  return response.data
}

export const getLedgerFilters = async ({ input }: QueryGetLedgerFiltersArgs) => {
  const {
    org_ids,
    user_id,
    ledger_type,
    has_outstanding_commissions,
    period_id,
    period_start_date,
    period_end_date,
    include_clawback_advisor_in_source,
  } = input ?? {}

  const queryParams: Record<string, unknown> = { org_id: org_ids, user_id }

  // Arrays are serialised as repeated bare keys (ledger_type=A&ledger_type=B)
  if (ledger_type !== undefined) queryParams.ledger_type = ledger_type
  if (has_outstanding_commissions !== undefined) queryParams.has_outstanding_commissions = has_outstanding_commissions
  if (period_id !== undefined) queryParams.period_id = period_id
  if (period_start_date !== undefined) queryParams.period_start_date = period_start_date
  if (period_end_date !== undefined) queryParams.period_end_date = period_end_date
  if (include_clawback_advisor_in_source !== undefined) {
    queryParams.include_clawback_advisor_in_source = include_clawback_advisor_in_source
  }

  const queryString = qs.stringify(queryParams, { arrayFormat: 'repeat' })

  const response: AxiosResponse<LedgerFilters> = await request.get<LedgerFilters>(
    `/accounting/ledger/filter${queryString ? `?${queryString}` : ''}`,
  )
  return response.data
}

interface RawProviderStatementFilters {
  provider_ids?: string[]
  club_ids?: string[]
}

export const getProviderStatementFilters = async ({ input }: QueryGetProviderStatementFiltersArgs) => {
  const { provider_ids, club_ids, period_ids, statuses } = input ?? {}

  const queryString = qs.stringify({ provider_ids, club_ids, period_ids, statuses }, { arrayFormat: 'repeat' })

  const response: AxiosResponse<RawProviderStatementFilters> = await requestV2.get<RawProviderStatementFilters>(
    `/accounting/statement/filter${queryString ? `?${queryString}` : ''}`,
  )

  // Map the raw REST response (snake_case id arrays) onto the GraphQL parent shape. Keep this
  // mapping isolated so a backend contract change is a single-line edit here — the id arrays are
  // hydrated into Organisations by the ProviderStatementFilters type resolver.
  return {
    providerIds: response.data.provider_ids ?? [],
    clubIds: response.data.club_ids ?? [],
  }
}

export const getLedgerSummary = async (input: QueryGetLedgerSummaryArgs) => {
  // extracted query parameters are sent as URL params
  const { org_id = [], user_id = [], totals_only, advisor_ids, if_due, ...bodyFilter } = input.input.filter || {}

  // build query parameters object excluding undefined/empty values
  const queryParams: Partial<LedgerSummaryFilterInput> = {
    totals_only: totals_only ?? false,
  }

  // you can filter by either user_id, advisor_ids or org_id but only one at a time
  if (user_id?.length) {
    queryParams.user_id = user_id
  } else if (advisor_ids?.length) {
    queryParams.user_id = advisor_ids
  } else if (org_id?.length) {
    queryParams.org_id = org_id
  }
  if (totals_only !== undefined) queryParams.totals_only = totals_only
  if (if_due !== undefined) queryParams.if_due = if_due

  const queryString =
    Object.keys(queryParams).length > 0 ? `?${qs.stringify(queryParams, { arrayFormat: 'repeat' })}` : ''

  const requestBody = {
    ...input.input,
    filter: Object.keys(bodyFilter).length > 0 ? bodyFilter : undefined,
  }

  try {
    const response: AxiosResponse<GetLedgerSummaryResponse> = await request.post<GetLedgerSummaryResponse>(
      `/accounting/ledger/summary${queryString}`,
      requestBody,
    )

    return response.data
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    throw new Error(`Failed to fetch ledger summary: ${errorMessage}`)
  }
}

export const getLedgers = async (input: QueryGetLedgersArgs) => {
  // Org ids is also a type of filter but BE accepts it as a query param instead of body
  const { orgIds = [], regulated_by, ...rest } = input.input.filter || {}

  const queryObj = {
    // If no org ids, then we don't wanna query by orgIds
    org_id: orgIds?.length ? orgIds : undefined,
    regulated_by,
    user_id: input.input.user_id,
  }

  const query = qs.stringify(queryObj, { arrayFormat: 'repeat' })

  const response = await request.post<GetLedgersResponse>(`/accounting/ledger${query ? '?' : ''}${query}`, {
    ...input.input,
    filter: rest,
  })
  return response.data
}

interface RawProviderStatement {
  id: number
  amount?: number
  provider_id?: string
  provider_name?: string
  club_name?: string
  period_id?: string
  status?: string
  number_of_transactions?: number
  number_of_items_to_match?: number
}

interface RawGetProviderStatementsResponse {
  statements?: RawProviderStatement[]
  next_page?: { bookmark?: string; pageSize?: number; page_size?: number }
}

export const getProviderStatements = async (
  input: QueryGetProviderStatementsArgs,
): Promise<GetProviderStatementsResponse> => {
  const response = await requestV2.post<RawGetProviderStatementsResponse>('/accounting/statement/provider', input.input)

  return {
    statements: (response.data.statements ?? []).map((statement) => ({
      id: String(statement.id),
      provider_id: statement.provider_id,
      provider_name: statement.provider_name,
      club_name: statement.club_name,
      period_id: statement.period_id,
      status: statement.status,
      total_amount: statement.amount,
      num_transactions: statement.number_of_transactions,
      items_to_match: statement.number_of_items_to_match,
    })),
    nextPage: response.data.next_page,
  }
}

interface RawRevenueEventDetails {
  advisor_name?: string
  client_names?: string[]
  completion_date?: string
  lender?: string
  loan_amount?: number
  policy_number?: string
  premium_amount?: number
  premium_frequency?: string
}

interface RawRevenueEvent {
  net_fee?: number
  gross_fee?: number
  initial_commission?: number
  renewal_commission?: number
  payment_direction?: string
  // Not yet returned upstream; mapped so it flows through the moment it is.
  payment_date?: string
}

interface RawRevenueEventCandidate {
  confidence_score?: number
  confirmed?: boolean
  details?: RawRevenueEventDetails
  revenue_event?: RawRevenueEvent
}

interface RawProviderStatementRow {
  id: number
  provider_statement_id?: number
  client_names?: string[]
  advisor_name?: string
  lender?: string
  loan_amount?: number
  proc_fee?: number
  initial_commission?: number
  renewal_commission?: number
  premium_amount?: number
  premium_frequency?: string
  policy_number?: string
  agency_code?: string
  payment_date?: string
  completion_date?: string
  payment_direction?: string
  confidence_score?: number
  match_confidence?: number
  reconciled_status?: string
  matches?: RawRevenueEventCandidate[]
  created_at?: string
  created_by?: string
  updated_at?: string
  updated_by?: string
  reconciled_at?: string
  reconciled_by?: string
}

interface RawProviderStatementDetailsResponse {
  statement_type?: string
  rows?: RawProviderStatementRow[]
  next_page?: { bookmark?: string; pageSize?: number; page_size?: number }
}

const toRevenueEventDetails = (details: RawRevenueEventDetails): RevenueEventDetails => ({
  advisor_name: details.advisor_name,
  client_names: details.client_names,
  completion_date: details.completion_date,
  lender: details.lender,
  loan_amount: details.loan_amount,
  policy_number: details.policy_number,
  premium_amount: details.premium_amount,
  premium_frequency: details.premium_frequency,
})

export const getProviderStatementDetails = async (
  input: QueryGetProviderStatementDetailsArgs,
): Promise<GetProviderStatementDetailsResponse> => {
  const response = await requestV2.post<RawProviderStatementDetailsResponse>(
    '/accounting/statement/provider_details',
    input.input,
  )

  return {
    statement_type: response.data.statement_type,
    rows: (response.data.rows ?? []).map((row) => ({
      id: String(row.id),
      provider_statement_id: row.provider_statement_id,
      client_names: row.client_names,
      advisor_name: row.advisor_name,
      lender: row.lender,
      loan_amount: row.loan_amount,
      proc_fee: row.proc_fee,
      initial_commission: row.initial_commission,
      renewal_commission: row.renewal_commission,
      premium_amount: row.premium_amount,
      premium_frequency: row.premium_frequency,
      policy_number: row.policy_number,
      agency_code: row.agency_code,
      payment_date: row.payment_date,
      completion_date: row.completion_date,
      payment_direction: row.payment_direction,
      confidence_score: row.confidence_score,
      match_confidence: row.match_confidence,
      reconciled_status: row.reconciled_status,
      created_at: row.created_at,
      created_by: row.created_by,
      updated_at: row.updated_at,
      updated_by: row.updated_by,
      reconciled_at: row.reconciled_at,
      reconciled_by: row.reconciled_by,
      matches: (row.matches ?? []).map((match) => ({
        confidence_score: match.confidence_score,
        confirmed: match.confirmed,
        details: match.details ? toRevenueEventDetails(match.details) : undefined,
        revenue_event: match.revenue_event
          ? {
              net_fee: match.revenue_event.net_fee,
              gross_fee: match.revenue_event.gross_fee,
              initial_commission: match.revenue_event.initial_commission,
              renewal_commission: match.revenue_event.renewal_commission,
              payment_direction: match.revenue_event.payment_direction,
              payment_date: match.revenue_event.payment_date,
            }
          : undefined,
      })),
    })),
    nextPage: response.data.next_page,
  }
}

// The MimeTypes enum lives in @acre/utils, which depends on @acre/graphql — importing it here
// would close a package-level cycle, so the single MIME type this module needs is declared locally.
const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

// Base64 and byte data are converted in chunks: a single String.fromCharCode(...bytes) spread over
// a multi-megabyte spreadsheet exceeds the maximum argument count and overflows the call stack.
const BINARY_CHUNK_SIZE = 8192

const base64ToBlob = (base64: string, mimeType: string): Blob => {
  const binary = atob(base64)
  const parts: BlobPart[] = []

  for (let offset = 0; offset < binary.length; offset += BINARY_CHUNK_SIZE) {
    const slice = binary.slice(offset, offset + BINARY_CHUNK_SIZE)
    const bytes = new Uint8Array(slice.length)

    for (let i = 0; i < slice.length; i++) {
      bytes[i] = slice.charCodeAt(i)
    }

    parts.push(bytes)
  }

  return new Blob(parts, { type: mimeType })
}

const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer)
  let binary = ''

  for (let offset = 0; offset < bytes.length; offset += BINARY_CHUNK_SIZE) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + BINARY_CHUNK_SIZE))
  }

  return btoa(binary)
}

const defaultTemplateFilename = (statementType: StatementType): string =>
  statementType === 'PROTECTION_PROVIDER_STATEMENT'
    ? 'protection-provider-statement-template.xlsx'
    : 'mortgage-provider-statement-template.xlsx'

// Wire shape of the 400 validation body. A cell error identifies its target as a `cell`, a
// `column`+`row` pair, or - for a whole column missing from the file - a `field` name alone.
// Parsing of this envelope is kept entirely inside uploadProviderStatement so a shape change (e.g.
// the payload nested under `errors`/`detail` rather than top-level) is a single-line edit here
// rather than a hunt across callers.
interface UploadProviderStatementErrorBody {
  total_error?: { expected?: number; got?: number; message?: string } | null
  cell_errors?: { column?: string; cell?: string; field?: string; message?: string; row?: number }[] | null
}

export const uploadProviderStatement = async ({
  input,
}: MutationUploadProviderStatementArgs): Promise<UploadProviderStatementResponse> => {
  const {
    statement_type,
    validation_total,
    club_id,
    provider_id,
    period_id,
    document_name,
    data_base64,
    skip_total_validation,
  } = input

  const formData = new FormData()
  formData.append('statement_type', statement_type)
  formData.append('validation_total', String(validation_total))

  // An unselected optional id must be absent from the request entirely, not sent as an empty value
  if (club_id) formData.append('club_id', club_id)
  if (provider_id) formData.append('provider_id', provider_id)
  if (period_id) formData.append('period_id', period_id)
  if (skip_total_validation) formData.append('skip_total_validation', String(skip_total_validation))

  formData.append('file', base64ToBlob(data_base64, XLSX_MIME_TYPE), document_name)

  try {
    // No explicit Content-Type: axios unsets it for FormData so the browser generates the multipart
    // boundary. Setting the header by hand yields a boundary-less type the server cannot parse.
    const response = await requestV2.post<{ id?: string | number; filename?: string }>(
      '/accounting/statement/upload',
      formData,
    )

    // Map the raw REST response onto the GraphQL shape here so a backend contract change is a
    // single-line edit rather than a spread that leaks unknown fields into the schema.
    return {
      statement_id: response.data?.id != null ? String(response.data.id) : null,
      filename: response.data?.filename ?? null,
      total_error: null,
      cell_errors: null,
    }
  } catch (e) {
    const err = e as AxiosError<UploadProviderStatementErrorBody>
    const totalError = err.response?.data?.total_error
    const cellErrors = err.response?.data?.cell_errors

    // A qualifying 400 is a validation outcome, not a transport failure: only a body that
    // actually carries usable total or cell errors takes this path, everything else rethrows.
    const hasTotalError = typeof totalError?.expected === 'number' && typeof totalError?.got === 'number'
    const hasCellErrors = Array.isArray(cellErrors) && cellErrors.length > 0

    if (err.response?.status === 400 && (hasTotalError || hasCellErrors)) {
      return {
        statement_id: null,
        filename: null,
        total_error: hasTotalError
          ? { expected: totalError?.expected, got: totalError?.got, message: totalError?.message ?? null }
          : null,
        cell_errors: hasCellErrors
          ? (cellErrors ?? []).map((cellError) => ({
              column: cellError.column,
              cell: cellError.cell,
              field: cellError.field,
              message: cellError.message,
              row: cellError.row,
            }))
          : null,
      }
    }

    throw e
  }
}

export const downloadProviderStatementTemplate = async ({
  input,
}: MutationDownloadProviderStatementTemplateArgs): Promise<ProviderStatementTemplate> => {
  const response = await requestV2.get<ArrayBuffer>('/accounting/statement/template', {
    params: { statement_type: input.statement_type },
    responseType: 'arraybuffer',
  })

  // The filename is derived rather than read from Content-Disposition: that header is not
  // guaranteed to be CORS-exposed, and a deterministic name beats a fallback chain.
  return {
    filename: defaultTemplateFilename(input.statement_type),
    content_base64: arrayBufferToBase64(response.data),
  }
}

// IDs in accounting are returned as numbers, not strings
export const deleteLedger = async (id: number) => {
  const response = await request.delete(`/accounting/ledger/${id}`, {
    validateStatus: function (status) {
      return status >= 200 && status < 500 // default
    },
  })

  // Accounting microservice doesn't throw error on a status < 500,
  // hence the need to throw the error from the data here
  if (response?.data?.error) {
    throw new GraphqlException(response.data.error)
  }

  return id
}

export const writeOffLedger = async (input: MutationWriteOffLedgerArgs) => {
  const response: AxiosResponse<Ledger> = await request.patch<Ledger>(
    `/accounting/ledger/${input.ledgerId}/write_off`,
    input.input,
  )
  return response.data
}

export const editLedgerPayment = async (input: MutationEditLedgerPaymentArgs) => {
  const response: AxiosResponse<Ledger> = await request.put<Ledger>(
    `/accounting/ledger/${input.ledgerId}/payment`,
    input.input,
  )
  return response.data
}

export const updatePayment = async (input: MutationUpdatePaymentArgs) => {
  const response = await request.patch<LedgerPayment>(
    `/accounting/ledger/${input.ledgerId}/payment/${input.paymentId}`,
    input.input,
  )
  return response.data
}

export const updateLedger = async (input: MutationUpdateLedgerPaymentArgs) => {
  const response = await request.patch<Ledger>(`/accounting/ledger/${input.ledgerId}`, input.input)
  return response.data
}

export const updateLedgersFullPayment = async ({ input }: MutationUpdateLedgersFullPaymentArgs) => {
  try {
    const response = await request.put<PutLedgersFullPaymentResponse>('/accounting/ledger/full_payment', input)
    return response.data
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    throw new Error(`Failed to update ledger(s) full payment: ${errorMessage}`)
  }
}

interface MarkLedgersPaidRequestPayment {
  ledger_id: number
  amount: number
  received_date?: string
}

export const markLedgersPaid = async ({ input }: MutationMarkLedgersPaidArgs): Promise<MarkLedgersPaidResponse> => {
  const payments: MarkLedgersPaidRequestPayment[] = input.payments.map(({ ledger_id, amount, received_date }) => ({
    ledger_id,
    amount,
    ...(received_date ? { received_date } : {}),
  }))

  try {
    const response = await request.post<MarkLedgersPaidResponse>('/accounting/ledgers/payments', { payments })
    return response.data
  } catch (error) {
    // A 400 (every item failed) or 500 (batch aborted partway through) still carries the
    // per-item results body — only rethrow when there is genuinely nothing usable to return.
    if (isAxiosError<MarkLedgersPaidResponse>(error) && error.response?.data?.results) {
      return error.response.data
    }

    throw error
  }
}

export const createRecurringPayment = async (input: MutationCreateRecurringPaymentArgs) => {
  const response = await request.post<RecurringLedger>(`/accounting/ledger/recurring`, input.input)
  return response.data
}

export const updateRecurringLedgerPayment = async (input: MutationUpdateRecurringLedgerPaymentArgs) => {
  const response = await request.patch<RecurringLedger>(`/accounting/ledger/recurring/${input.ledgerId}`, input.input)
  return response.data
}

export const createLedgerPayment = async (input: MutationCreateLedgerPaymentArgs) => {
  const response = await request.post<OneOffLedger>(`/accounting/ledger/create`, input.input)
  return response.data
}

export const initiateGoCardlessPayment = async (input: MutationInitiateGoCardlessPaymentArgs) => {
  try {
    // The backend supports multiple client_id parameters using collectionFormat: "multi"
    // We'll build the URL with multiple client_id params from the clientIds array
    const clientIds = input.input?.clientIds || []
    if (clientIds.length === 0) {
      throw new Error('At least one client ID is required')
    }

    const clientIdParams = clientIds.map((id) => `client_id=${encodeURIComponent(id)}`).join('&')
    const response = await request.post(
      `/accounting/go-cardless/initiate-payment?${clientIdParams}&ledger=${input.input?.ledgerID}`,
      undefined,
    )
    return response.data
  } catch (e) {
    const ex = e as AxiosError<{ error: string }>
    if (ex.response?.data?.error) {
      throw new Error(ex.response.data?.error)
    }
    throw new Error('Could not initiate payment')
  }
}

export const getGoCardlessAuthURL = async () => {
  try {
    const response = await request.get(`/accounting/go-cardless/generate-auth-url`)
    return response.data
  } catch (e) {
    const ex = e as AxiosError<{ error: string }>
    if (ex.response?.data?.error) {
      throw new Error(ex.response.data?.error)
    }
    throw new Error('Could not generate authorisation URL')
  }
}

export const getGoCardlessAuthCallback = async (input: MutationGoCardlessAuthCallbackArgs) => {
  if (!input.input?.code || !input.input?.state) {
    throw new Error('Invalid parameters')
  }
  try {
    await request.get(`/accounting/go-cardless/auth-callback?code=${input.input?.code}&state=${input.input?.state}`)
    return 'OK'
  } catch (e) {
    const ex = e as AxiosError<{ error: string }>
    if (ex.response?.data?.error) {
      throw new Error(ex.response.data?.error)
    }
    throw new Error('Could not receive callback')
  }
}

export const getGoCardlessAccountStatus = async () => {
  try {
    const response = await request.get(`/accounting/go-cardless/account-status`)
    return response.data
  } catch (e) {
    const ex = e as AxiosError<{ error: string }>
    if (ex.response?.data?.error) {
      throw new Error(ex.response.data?.error)
    }
    throw new Error('Could not get account status')
  }
}

export const hasGoCardlessMandate = async (input: QueryHasGoCardlessMandateArgs) => {
  if (!input.client_id) {
    throw new Error('Invalid parameters')
  }
  try {
    const response = await request.get(`/accounting/go-cardless/has-mandate?client_id=${input.client_id}`)
    return response.data
  } catch (e) {
    const ex = e as AxiosError<{ error: string }>
    if (ex.response?.data?.error) {
      throw new Error(ex.response.data?.error)
    }
    throw new Error('Could not get mandate status')
  }
}
